import React, { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Box, CheckCircle2, ChevronRight, Download, ExternalLink, FileSpreadsheet, FileText, Send } from 'lucide-react';
import type {
  ProjectFile,
  ProjectFileStream,
  RfqHubFileRelease,
  RfqPackage,
  RfqSubmission,
  VendorFileRelease,
} from '../../types';
import {
  buildRfqGroups,
  countRfqFiles,
  defaultCollapsedRfqGroupKeys,
  RFQ_SUPPLIER_STATUS_LABELS,
  rfqEntryKindCategory,
  rfqKindCategoryLabel,
  rfqSupplierStatus,
  UNSENT_SUPPLIER_ID,
  type RfqFileEntry,
  type RfqKindCategory,
  type RfqProjectApprovalFields,
  type RfqSupplierStatus,
} from '../../utils/projectRfqFiles';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { workTypeLabel } from '../../utils/vendorHelpers';
import './ProjectFilesViews.css';

export interface ProjectRfqFilesViewProps {
  hubReleases: RfqHubFileRelease[];
  vendorReleases: VendorFileRelease[];
  packages: RfqPackage[];
  streams: ProjectFileStream[];
  files: ProjectFile[];
  structures: StructureRef[];
  supplierNames: Map<string, string>;
  /** Quote submissions for the deal — drives the Sent / Quoted / Approved / Not bidding chip. */
  submissions?: RfqSubmission[];
  /** Approval fields of the deal's RFQ work orders, keyed by work order id. */
  projectApprovals?: Map<string, RfqProjectApprovalFields>;
  loading: boolean;
  /** Feed failure (index / permission). Shown instead of a misleading empty state. */
  error?: string | null;
  onRetry?: () => void;
  onOpenSource: (file: ProjectFile) => void;
  onOpenStream: (streamId: string) => void;
  /** Jump to the Suppliers tab (package-only rows have no file to open). */
  onOpenSuppliers?: () => void;
}

function formatSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function entryIcon(e: RfqFileEntry) {
  if (e.sourceStreamKind === 'timber_list_csv' || e.sourceStreamKind === 'hardware_list_csv') {
    return <FileSpreadsheet size={15} />;
  }
  if (e.sourceStreamKind === 'three_d_raw' || e.kind === 'vendor') return <Box size={15} />;
  return <FileText size={15} />;
}

const ProjectRfqFilesView: React.FC<ProjectRfqFilesViewProps> = ({
  hubReleases,
  vendorReleases,
  packages,
  streams,
  files,
  structures,
  supplierNames,
  submissions = [],
  projectApprovals,
  loading,
  error,
  onRetry,
  onOpenSource,
  onOpenStream,
  onOpenSuppliers,
}) => {
  const emptyApprovals = useMemo(() => new Map<string, RfqProjectApprovalFields>(), []);
  const approvalsById = projectApprovals ?? emptyApprovals;
  const groups = useMemo(
    () =>
      buildRfqGroups({
        hubReleases,
        vendorReleases,
        packages,
        streams,
        files,
        structures,
        supplierNames,
        submissions,
      }),
    [hubReleases, vendorReleases, packages, streams, files, structures, supplierNames, submissions],
  );
  const total = useMemo(() => countRfqFiles(groups), [groups]);
  const kindTotals = useMemo(() => {
    let timber = 0;
    let cnc = 0;
    for (const g of groups) {
      for (const s of g.suppliers) {
        for (const e of s.entries) {
          if (rfqEntryKindCategory(e) === 'cnc') cnc += 1;
          else timber += 1;
        }
      }
    }
    return { timber, cnc };
  }, [groups]);
  const RFQ_KIND_ORDER: RfqKindCategory[] = ['timber', 'cnc'];
  const fileById = useMemo(() => {
    const m = new Map<string, ProjectFile>();
    for (const f of files) if (f.id) m.set(f.id, f);
    return m;
  }, [files]);
  const submissionById = useMemo(() => {
    const m = new Map<string, RfqSubmission>();
    for (const s of submissions) if (s.id) m.set(s.id, s);
    return m;
  }, [submissions]);
  /** Total of the quote(s) a structure-scoped row stands for — makes each approval identifiable. */
  const rowQuoteTotal = (e: RfqFileEntry): number | null => {
    if (!e.submissionIds?.length) return null;
    let sum = 0;
    let any = false;
    for (const id of e.submissionIds) {
      const t = submissionQuoteTotal(submissionById.get(id));
      if (t != null) {
        sum += t;
        any = true;
      }
    }
    return any ? sum : null;
  };
  // Whole project starts folded once structure-scoped sends exist; `toggled` holds
  // the sections the user flipped away from that default.
  const defaultCollapsed = useMemo(() => defaultCollapsedRfqGroupKeys(groups), [groups]);
  const [toggled, setToggled] = useState<Set<string>>(() => new Set());
  const isOpen = (key: string) => defaultCollapsed.has(key) === toggled.has(key);
  const toggle = (key: string) =>
    setToggled(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (error && total === 0) {
    return (
      <div className="pfv-empty pfv-empty--error" data-tour="pf-rfqs" role="alert">
        <p className="pfv-empty-title">Couldn't load RFQ history</p>
        <p className="pfv-empty-hint">
          Supplier releases could not be read right now, so this list may be incomplete. {error}
        </p>
        {onRetry ? (
          <button type="button" className="pfv-act" onClick={onRetry}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (!loading && total === 0) {
    return (
      <div className="pfv-empty" data-tour="pf-rfqs">
        <p className="pfv-empty-title">Nothing sent to suppliers yet</p>
        <p className="pfv-empty-hint">
          RFQs are the timber lists, 3D models and CNC files released to providers. Use{' '}
          <strong>Release to suppliers</strong> or <strong>Release to vendor</strong> on a track in
          Deliverables; each staged copy appears here by structure and supplier.
        </p>
      </div>
    );
  }

  const statusChip = (status: RfqSupplierStatus | null, count: number) => {
    if (!status) {
      return count > 0 ? (
        <span className="pfv-chip pfv-chip--current" title={`${count} package${count !== 1 ? 's' : ''} published`}>
          <Send size={10} aria-hidden /> Sent
        </span>
      ) : null;
    }
    const label = RFQ_SUPPLIER_STATUS_LABELS[status];
    const title =
      status === 'approved'
        ? 'A quote on this file was approved'
        : status === 'quoted'
          ? 'Supplier uploaded a quote — awaiting review'
          : status === 'not_bidding'
            ? 'Supplier declined to quote this file'
            : `${count} package${count !== 1 ? 's' : ''} published — no response yet`;
    return (
      <span className={`pfv-chip pfv-chip--rfq-${status}`} title={title}>
        {status === 'approved' ? <CheckCircle2 size={10} aria-hidden /> : status === 'sent' ? <Send size={10} aria-hidden /> : null}{' '}
        {label}
      </span>
    );
  };

  const renderEntry = (e: RfqFileEntry, supplierOrgId: string) => {
    const source = fileById.get(e.sourceFileId);
    const status = rfqSupplierStatus({
      entry: e,
      supplierOrgId,
      packages,
      submissions,
      projectById: approvalsById,
    });
    const quoteTotal = rowQuoteTotal(e);
    return (
      <li key={`${e.kind}:${e.id}${e.submissionIds?.length ? `:${e.submissionIds.join(',')}` : ''}`} className="pfv-row">
        <span className="pfv-row-icon" aria-hidden>
          {entryIcon(e)}
        </span>
        <div className="pfv-row-main">
          <div className="pfv-row-title-line">
            <span className="pfv-row-title" title={e.fileName}>
              {e.displayLabel}
            </span>
            {e.releaseVersionIndex > 0 ? (
              <span className="pfv-chip pfv-chip--version">v{e.releaseVersionIndex}</span>
            ) : null}
            <span
              className={`pfv-chip pfv-chip--rfq-kind pfv-chip--rfq-kind-${rfqEntryKindCategory(e)}`}
              title={rfqEntryKindCategory(e) === 'cnc' ? 'CNC quote or vendor copy' : 'Timber list or timber RFQ'}
            >
              {e.kind === 'vendor' ? 'CNC vendor copy' : workTypeLabel(e.workType)}
            </span>
            {e.fromProposal ? (
              <span className="pfv-chip pfv-chip--structure" title="Timber list came straight from the proposal, not a Project Hub file">
                Proposal timber list
              </span>
            ) : null}
            {e.packageIds.length > 0 ? (
              statusChip(status, e.packageIds.length)
            ) : e.kind === 'hub' ? (
              <span className="pfv-chip pfv-chip--pending">Staged</span>
            ) : null}
            {quoteTotal != null ? (
              <span
                className="pfv-chip pfv-chip--version"
                title={
                  status === 'approved'
                    ? 'Approved quote for the structures in this section'
                    : 'Quote for the structures in this section'
                }
              >
                {formatQuoteTotal(quoteTotal)}
              </span>
            ) : null}
            {e.structureNames?.length ? (
              <span className="pfv-chip pfv-chip--structure" title="Structures the staged file covers">
                {e.structureNames.filter(Boolean).join(' + ')}
              </span>
            ) : null}
            {e.linkedProposalVersionLabel && e.kind !== 'package' ? (
              <span className="pfv-chip pfv-chip--structure" title="Linked proposal version">
                {e.linkedProposalVersionLabel}
              </span>
            ) : null}
          </div>
          <div className="pfv-row-meta">
            {e.sourceStreamId ? (
              <button type="button" className="pfv-link" onClick={() => onOpenStream(e.sourceStreamId)}>
                {e.sourceStreamTitle || 'Source track'}
              </button>
            ) : (
              <span>{e.sourceStreamTitle || 'Source track'}</span>
            )}
            {e.versionLabel ? <> · {e.versionLabel}</> : null}
            {e.versionIndex != null ? <> · #{e.versionIndex}</> : null}
            {' · '}
            {e.releasedByName || 'Unknown'} · {formatDistanceToNow(e.createdAt, { addSuffix: true })}
            {e.sizeBytes ? <> · {formatSize(e.sizeBytes)}</> : null}
          </div>
        </div>
        <div className="pfv-row-acts">
          {source ? (
            <button type="button" className="pfv-act" onClick={() => onOpenSource(source)}>
              Open
            </button>
          ) : e.releasedUrl ? (
            <a className="pfv-act" href={e.releasedUrl} target="_blank" rel="noopener noreferrer">
              Open <ExternalLink size={11} aria-hidden />
            </a>
          ) : e.kind === 'package' && onOpenSuppliers ? (
            <button type="button" className="pfv-act" onClick={onOpenSuppliers} title="Open the Suppliers tab for this package">
              Open in Suppliers
            </button>
          ) : null}
          {e.releasedUrl ? (
            <a
              className="pfv-act pfv-act--muted"
              href={e.releasedUrl}
              download={e.fileName}
              target="_blank"
              rel="noopener noreferrer"
              title="Download the exact copy the supplier received"
            >
              <Download size={11} aria-hidden /> Supplier copy
            </a>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <div className="pfv" data-tour="pf-rfqs">
      {error ? (
        <div className="pfv-feed-warning" role="alert">
          <span>Part of the supplier history could not be loaded — this list may be incomplete.</span>
          {onRetry ? (
            <button type="button" className="pfv-act" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="pfv-summary">
        <span className="pfv-stat">{total} file{total !== 1 ? 's' : ''}</span>
        <span className="pfv-stat pfv-stat--timber">{kindTotals.timber} timber</span>
        <span className="pfv-stat pfv-stat--cnc">{kindTotals.cnc} CNC</span>
        <span className="pfv-stat">{groups.length} RFQ scope{groups.length !== 1 ? 's' : ''}</span>
        <span className="pfv-summary-note">
          Read-only — grouped by structure scope, then supplier, with timber and CNC in separate sections. Stage or re-stage from
          Deliverables.
        </span>
      </div>
      {groups.map(g => {
        const key = g.structureId;
        const open = isOpen(key);
        return (
          <section key={key} className="pfv-group">
            <button type="button" className="pfv-group-toggle" aria-expanded={open} onClick={() => toggle(key)}>
              <ChevronRight size={14} className={`pfv-caret${open ? ' pfv-caret--open' : ''}`} aria-hidden />
              <span className="pfv-group-title" title={g.structureNames.length > 1 ? g.structureNames.join(', ') : undefined}>
                {g.structureName}
              </span>
              {g.structureIds.length > 1 ? (
                <span className="pfv-group-sub">{g.structureIds.length} structures</span>
              ) : null}
              <span className="pfv-group-count">{g.total}</span>
            </button>
            {open ? (
              <div className="pfv-group-body">
                {g.suppliers.map(s => (
                  <div key={s.supplierOrgId} className="pfv-subgroup">
                    <h4 className={`pfv-subgroup-title${s.supplierOrgId === UNSENT_SUPPLIER_ID ? ' pfv-subgroup-title--muted' : ''}`}>
                      {s.supplierName}
                    </h4>
                    {RFQ_KIND_ORDER.map(kind => {
                      const entries = s.entries.filter(e => rfqEntryKindCategory(e) === kind);
                      if (entries.length === 0) return null;
                      return (
                        <div key={kind} className="pfv-rfq-category">
                          <div className={`pfv-rfq-category-head pfv-rfq-category-head--${kind}`}>
                            <span>{rfqKindCategoryLabel(kind)}</span>
                            <span className="pfv-rfq-category-count">{entries.length}</span>
                          </div>
                          <ul className="pfv-list">{entries.map(e => renderEntry(e, s.supplierOrgId))}</ul>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

export default ProjectRfqFilesView;
